import type { JSX } from 'react';
import { FormSection, Radio } from '@ksyos/design-system';
import type { BariPreNurseSpecialistMedScreeningReportData } from '@common/validation/customControls/bariPre/bariPreNurseSpecialistMedScreeningReport/types';
import { VitaminedeficientiesOpties } from '@common/validation/customControls/bariPre/bariPreNurseSpecialistMedScreeningReport/bariPreNurseSpecialistMedScreeningReportData';
import { useProcessActivityEditContext } from '@frontend/utils';
import {
    BooleanRadioGroupFormGroup,
    RadioGroupFormGroup,
    TextAreaFormGroup,
    TextInputFormGroup,
} from '../../processActivityFormGroupFields';
import { YesNoWithConditionalTextAreaFormGroup } from '../../YesNoWithConditionalTextAreaFormGroup';

export function BariPreNurseSpecialistMedScreeningReportAnamnese(): JSX.Element {
    const { data, onChange } = useProcessActivityEditContext<BariPreNurseSpecialistMedScreeningReportData>();

    return (
        <FormSection title="Anamnese">
            <TextAreaFormGroup
                fieldKey="medische_voorgeschiedenis"
                isRequired={true}
                label="Medische voorgeschiedenis"
            />
            <YesNoWithConditionalTextAreaFormGroup
                fieldKey="eerdere_operaties"
                fieldValue={data.eerdere_operaties}
                isRequired={true}
                label="Eerdere operaties"
                textAreaFieldKey="eerdere_operaties_toelichting"
                textAreaLabel="Toelichting eerdere operaties"
            />
            <YesNoWithConditionalTextAreaFormGroup
                fieldKey="consult_internist_gehad"
                fieldValue={data.consult_internist_gehad}
                isClearable={true}
                label="Consult bij internist gehad"
                textAreaFieldKey="consult_internist_toelichting"
                textAreaLabel="Toelichting internist"
            />
            <YesNoWithConditionalTextAreaFormGroup
                fieldKey="consult_MDL_arts_gehad"
                fieldValue={data.consult_MDL_arts_gehad}
                isClearable={true}
                label="Consult bij MDL-arts gehad"
                textAreaFieldKey="consult_MDL_arts_toelichting"
                textAreaLabel="Toelichting MDL-arts"
            />
            <RadioGroupFormGroup
                fieldKey="vitaminedeficienties"
                isRequired={true}
                label="Vitaminedeficiënties"
                onChange={handleChangeVitaminedeficienties}
            >
                {Object.values(VitaminedeficientiesOpties).map((value) => (
                    <Radio
                        key={value}
                        label={value}
                        value={value}
                    />
                ))}
            </RadioGroupFormGroup>
            {data.vitaminedeficienties === VitaminedeficientiesOpties.ja && (
                <TextAreaFormGroup
                    fieldKey="vitaminedeficienties_toelichting"
                    isRequired={true}
                    label="Toelichting vitaminedeficiënties"
                />
            )}

            <BooleanRadioGroupFormGroup
                fieldKey="helicobacter_pylori_onderzoek_gehad"
                isRequired={true}
                label="Helicobacter pylori onderzoek gehad"
                onChange={handleChangeHelicobacterPyloriOnderzoekGehad}
                type="radio"
            />
            {data.helicobacter_pylori_onderzoek_gehad && (
                <>
                    <TextInputFormGroup
                        fieldKey="helicobacter_pylori_onderzoek_datum"
                        isRequired={true}
                        label="Datum onderzoek"
                        tooltipText="Maand en jaartal"
                    />
                    <BooleanRadioGroupFormGroup
                        fieldKey="helicobacter_pylori_sprake_van"
                        isRequired={true}
                        label="Sprake van helicobacter pylori"
                        onChange={handleChangeHelicobacterPyloriSprakeVan}
                        type="radio"
                    />
                    {data.helicobacter_pylori_sprake_van && (
                        <>
                            <BooleanRadioGroupFormGroup
                                fieldKey="helicobacter_pylori_behandeld_met_antibiotica"
                                isRequired={true}
                                label="Behandeld met antibiotica"
                                onChange={handleChangeHelicobacterPyloriBehandeldMetAntibiotica}
                                type={'radio'}
                            />
                            {data.helicobacter_pylori_behandeld_met_antibiotica && (
                                <TextAreaFormGroup
                                    fieldKey="helicobacter_pylori_behandeld_met_antibiotica_type"
                                    label="Antibiotica type"
                                />
                            )}
                        </>
                    )}
                </>
            )}
        </FormSection>
    );

    async function handleChangeVitaminedeficienties(value: string | null): Promise<void> {
        return onChange(
            {
                vitaminedeficienties: value as VitaminedeficientiesOpties,
                vitaminedeficienties_toelichting: null,
            },
            'vitaminedeficienties'
        );
    }

    async function handleChangeHelicobacterPyloriOnderzoekGehad(value: boolean | null): Promise<void> {
        return onChange(
            {
                helicobacter_pylori_behandeld_met_antibiotica: null,
                helicobacter_pylori_behandeld_met_antibiotica_type: null,
                helicobacter_pylori_onderzoek_datum: null,
                helicobacter_pylori_onderzoek_gehad: value,
                helicobacter_pylori_sprake_van: null,
            },
            'helicobacter_pylori_onderzoek_gehad'
        );
    }

    async function handleChangeHelicobacterPyloriSprakeVan(value: boolean | null): Promise<void> {
        return onChange(
            {
                helicobacter_pylori_behandeld_met_antibiotica: null,
                helicobacter_pylori_behandeld_met_antibiotica_type: null,
                helicobacter_pylori_sprake_van: value,
            },
            'helicobacter_pylori_sprake_van'
        );
    }

    async function handleChangeHelicobacterPyloriBehandeldMetAntibiotica(value: boolean | null): Promise<void> {
        return onChange(
            {
                helicobacter_pylori_behandeld_met_antibiotica: value,
                helicobacter_pylori_behandeld_met_antibiotica_type: null,
            },
            'helicobacter_pylori_behandeld_met_antibiotica'
        );
    }
}
